import styled from 'styled-components'
import { Trans } from 'react-i18next'
import { useTranslation } from 'next-i18next'

import { CheckoutQA } from 'shared/dataAttributes'
import { FCLink } from 'shared/components/Links'
import { colors } from 'shared/lib'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useUser } from 'shared/hooks/useUser'

const TP = 'checkout.components.ExpressCheckoutPanel.ExpressCheckout'

interface ExpressCheckoutProps {
  noTopMargin?: boolean
  forceTopMargin?: boolean
}

const ExpressCheckout = ({ noTopMargin, forceTopMargin }: ExpressCheckoutProps) => {
  const { applePayAvailable, ApplePayButton } = useCheckoutContext()
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const { currentUser } = useUser()
  const { t } = useTranslation()

  if (!applePayAvailable) {
    return null
  }

  const isGuest = currentUser?.isGuest
  /**
   * There's about 10 different places where express checkout is shown. Whether the top
   * margin is necessary depends on where it is rendered and it depends on the state of
   * the guest_checkout feature flag, the user login state, and the progress within the
   * checkout flow.
   *
   * @todo: Refactor after guest checkout is removed when there are fewer
   * usages of this. Perhaps the margin should be managed by the component rendering
   * this as well.
   */
  let Wrapper =
    noTopMargin || (isGuestCheckoutEnabled && isGuest) ? ExpressGuestWrapper : ExpressWrapper
  if (forceTopMargin) {
    Wrapper = ExpressWrapper
  }

  return (
    <>
      {ApplePayButton && (
        <Wrapper>
          <ApplePayButton />
          <Disclaimer data-qa={CheckoutQA.ExpressDisclaimerText}>
            <Trans
              i18nKey={`${TP}.withThreeRetail`}
              t={t}
              defaults="By purchasing, I acknowledge that I have read and agree to the <0>Terms & Conditions</0> and <1>the Privacy Policy</1>."
              components={[
                <FCLink
                  href="/terms-conditions"
                  newTab
                  qaAttr={CheckoutQA.ExpressTermsConditionsLink}
                />,
                <FCLink href="/privacy" newTab qaAttr={CheckoutQA.ExpressPrivacyPolicyLink} />,
              ]}
            />
          </Disclaimer>
        </Wrapper>
      )}
    </>
  )
}

const ExpressWrapper = styled.div`
  margin-top: 20px;
  min-height: 70px;
`

const ExpressGuestWrapper = styled.div`
  min-height: 70px;
`

const Disclaimer = styled.div`
  color: ${colors.FC2_GREY};
  font-size: 12px;
  margin-top: 20px;
`

export default ExpressCheckout
